"use client";
import React, { useMemo, useState } from "react";
import AnalyticsSidebar from "../AnalyticsSidebar";
import { Navbar } from "../../../components/navbar";
import { Footer } from "../../../components/footer";
import Table from "../../../components/Table";
import styles from "../css/DrugStockLevel.module.css";
import FilterButtons from "../../../components/universal-filter-tabs.jsx";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const chartDataDispensary = [
  { name: "Amoxil 250mg", reordered: 150, live: 90 },
  { name: "Panadol 500mg", reordered: 120, live: 120 },
  { name: "Prilosec 20mg", reordered: 130, live: 110 },
  { name: "Claritin 10mg", reordered: 80, live: 45 },
  { name: "Aspirin 100mg", reordered: 90, live: 70 },
  { name: "Zyrtec 10mg", reordered: 60, live: 85 },
  { name: "Metformin 500mg", reordered: 140, live: 95 },
  { name: "Vitamin C 500mg", reordered: 75, live: 65 },
  { name: "Ibuprofen 200mg", reordered: 110, live: 98 },
  { name: "Loratadine 10mg", reordered: 95, live: 80 },
];
const chartDataFrontShop = [
  { name: "Bandages", reordered: 40, live: 120 },
  { name: "Thermometer", reordered: 20, live: 60 },
  { name: "Hand Sanitizer", reordered: 100, live: 200 },
  { name: "Face Mask", reordered: 80, live: 160 },
];
const chartDataClinic = [
  { name: "Syringes", reordered: 250, live: 230 },
  { name: "Gloves", reordered: 600, live: 540 },
  { name: "Saline", reordered: 120, live: 110 },
];
const chartDataOTC = [
  { name: "Vitamin D", reordered: 180, live: 160 },
  { name: "Omega 3", reordered: 140, live: 155 },
  { name: "Calcium", reordered: 110, live: 100 },
];

const columns = [
  { header: "Drug Name", accessor: "drug" },
  { header: "Active ingredient", accessor: "ingredient" },
  { header: "Nappi Code", accessor: "nappi" },
  { header: "Qty", accessor: "qty" },
  { header: "Expiry Date", accessor: "expiry" },
  {
    header: "Status",
    accessor: "status",
    render: (value) => (
      <span
        className={`${styles.statusBadge} ${value === "In Stock"
            ? styles.inStock
            : value === "Low Stock"
              ? styles.lowStock
              : styles.outStock
          }`}
      >
        {value}
      </span>
    ),
  },
  { header: "Reorder Level", accessor: "reorder" },
];

const tableDataDispensary = [
  {
    drug: "Amoxil 250mg",
    ingredient: "Amoxicillin",
    nappi: "716170019",
    qty: 150,
    expiry: "2025-10-20",
    status: "In Stock",
    reorder: 149,
  },
  {
    drug: "Panadol 500mg",
    ingredient: "Paracetamol",
    nappi: "716170419",
    qty: 45,
    expiry: "2024-08-15",
    status: "Low Stock",
    reorder: 783,
  },
  {
    drug: "Claritn 10mg",
    ingredient: "Loratadine",
    nappi: "716170919",
    qty: 8,
    expiry: "2024-06-30",
    status: "Out of Stock",
    reorder: 454,
  },
  {
    drug: "Prilosec 20mg",
    ingredient: "Omeprazole",
    nappi: "716174019",
    qty: 15,
    expiry: "2024-07-22",
    status: "Low Stock",
    reorder: 728,
  },
];
const tableDataFrontShop = [
  { drug: "Bandages", ingredient: "Gauze", nappi: "-", qty: 320, expiry: "-", status: "In Stock", reorder: 40 },
  { drug: "Thermometer", ingredient: "Digital", nappi: "-", qty: 56, expiry: "-", status: "In Stock", reorder: 12 },
];
const tableDataClinic = [
  { drug: "Syringes", ingredient: "3ml", nappi: "-", qty: 400, expiry: "2026-01-01", status: "In Stock", reorder: 50 },
  { drug: "Gloves", ingredient: "Latex", nappi: "-", qty: 1200, expiry: "2026-04-01", status: "In Stock", reorder: 200 },
];
const tableDataOTC = [
  { drug: "Vitamin D", ingredient: "Cholecalciferol", nappi: "-", qty: 260, expiry: "2026-10-01", status: "In Stock", reorder: 60 },
  { drug: "Omega 3", ingredient: "Fish Oil", nappi: "-", qty: 180, expiry: "2026-08-15", status: "In Stock", reorder: 40 },
];

export default function DrugStockLevel() {
  const [activeSegment, setActiveSegment] = useState("Dispensary");

  const chartData = useMemo(() => {
    if (activeSegment === "Front Shop") return chartDataFrontShop;
    if (activeSegment === "Clinic") return chartDataClinic;
    if (activeSegment === "OTC") return chartDataOTC;
    return chartDataDispensary;
  }, [activeSegment]);

  const tableData = useMemo(() => {
    if (activeSegment === "Front Shop") return tableDataFrontShop;
    if (activeSegment === "Clinic") return tableDataClinic;
    if (activeSegment === "OTC") return tableDataOTC;
    return tableDataDispensary;
  }, [activeSegment]);

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar backTo="/analytics" />

        <div className={`${styles.contentArea} analytics-content`}>
          <AnalyticsSidebar />
          <div className={styles.contentAreaFull}>
            <section className={styles.panel}>
              <div style={{ display: 'flex', width: '100%', justifyContent: 'center', marginBottom: '0.75rem' }}>
                <FilterButtons
                  buttons={["Dispensary", "Front Shop", "Clinic", "OTC"]}
                  activeButton={activeSegment}
                  onButtonClick={setActiveSegment}
                />
              </div>
              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <h3>Drug Stock Levels</h3>
                  <p>Comparison of reorder and live stock levels for various drugs.</p>
                </div>
                <div className={styles.chartWrap}>
                  <ResponsiveContainer width="100%" height={360}>
                    <BarChart data={chartData} barCategoryGap={12} barGap={2} margin={{ top: 24, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend verticalAlign="top" align="center" wrapperStyle={{ top: 0 }} />
                      <Bar dataKey="reordered" name="Reordered Stock Level" fill="#38BDF8" radius={[4, 4, 0, 0]} barSize={28} />
                      <Bar dataKey="live" name="Live Stock Level" fill="#8B5CF6" radius={[4, 4, 0, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}
